import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useRef, useState, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import Toolbar from './Toolbar';
import ToolbarEnabler from './ToolbarEnabler';

const TOOLBAR_WIDTH = 345;
const TOOLBAR_OFFSET = 12;

const ToolbarEditor = forwardRef((props, ref) => {
  /* Props */
  const {
    editorContainerEl,
    onClickInside,
    selectedText,
  } = props;

  /* Refs */
  const wrapperRef = useRef();
  const indicatorRef = useRef();
  const toolbarRef = useRef();

  /* State */
  const [toolbarBounds, setToolbarBounds] = useState(null);
  const [arrowBounds, setArrowBounds] = useState(null);
  const [isToolbarExpanded, setIsToolbarExpanded] = useState(false);
  const [isIndicatorVisible, setIsIndicatorVisible] = useState(false);

  /* Selectors */
  const isDisplayed = useSelector((store) => store.toolbar.isDisplayed);
  const currentBounds = useSelector((store) => store.toolbar.currentBounds);
  const isExpanded = useSelector((store) => store.toolbar.isExpanded);
  const hasChanges = useSelector((store) => store.toolbar.hasChanges);

  useEffect(() => {
    if (!isToolbarExpanded && isExpanded) {
      setIsToolbarExpanded(true)
    }
  }, [isExpanded, isToolbarExpanded]);

  useEffect(() => {
    if (currentBounds) {
      let x = currentBounds.left - (TOOLBAR_WIDTH / 2);
      // Check left edge
      x = x > TOOLBAR_OFFSET ? x : -TOOLBAR_OFFSET;

      // Check right edge
      x = x + TOOLBAR_WIDTH > editorContainerEl.clientWidth
        ? editorContainerEl.clientWidth - TOOLBAR_WIDTH + TOOLBAR_OFFSET
        : x;

      let y = currentBounds.bottom;

      if (isExpanded) {
        x = -TOOLBAR_OFFSET;
      }

      setToolbarBounds({ x, y });

      let xArrow = currentBounds.left > editorContainerEl.clientWidth
        ? editorContainerEl.clientWidth
        : currentBounds.left;

      setArrowBounds({
        ...currentBounds,
        x: xArrow,
        y: currentBounds.bottom,
      })
    }
  }, [currentBounds, editorContainerEl.clientWidth, isExpanded]);

  useEffect(() => {
    setIsIndicatorVisible(Boolean(selectedText && isDisplayed));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    escapeToolbarHandler: () => {
      if (!isIndicatorVisible && !hasChanges) {
        setIsIndicatorVisible(true);
        return { skipToolbarClose: true };
      }

      toolbarRef?.current?.escapeHandler();

      return { skipToolbarClose: false };
    },
    executeOptionHandler: toolbarRef?.current?.executeOptionHandler,
  }), [isIndicatorVisible, hasChanges]);

  const handleIndicatorClick = useCallback(() => {
    setIsIndicatorVisible(false);
  }, []);

  const isVisible = Boolean(isDisplayed && toolbarBounds);

  if (isIndicatorVisible) {
    return (
      <CSSTransition
        in={isVisible}
        timeout={2000}
        classNames='fade'
        nodeRef={indicatorRef}
      >
        <ToolbarEnabler
          bounds={arrowBounds}
          onClickOutside={props.onClickOutside}
          onClick={handleIndicatorClick}
          ref={indicatorRef}
        />
      </CSSTransition>
    )
  }
  
  return (
    <CSSTransition
      in={isVisible}
      timeout={2000}
      classNames='fade'
      nodeRef={wrapperRef}
    >
      <>
        <TopArrow bounds={arrowBounds} />
        <Wrapper
          ref={wrapperRef}
          arrowBounds={arrowBounds}
          bounds={toolbarBounds}
          isExpanded={isToolbarExpanded}
          onClick={onClickInside}
        >
          <Toolbar ref={toolbarRef} {...props} />
        </Wrapper>
      </>
    </CSSTransition>
  );
});

export default ToolbarEditor;

ToolbarEditor.propTypes = {
  editorContainerEl: PropTypes.object,
  onClickInside: PropTypes.func
};

const Wrapper = styled.div`
  position: absolute;
  padding: 6px;
  background-color: var(--lizzy-white);
  border-radius: 8px;
  left: 0;
  top: 0;
  z-index: 12;
  width: ${p => p.isExpanded ? `calc(100% + ${TOOLBAR_OFFSET * 2}px)` : `${TOOLBAR_WIDTH}px`};
  left: ${p => p.bounds?.x || 0}px;
  top: ${p => (p.bounds?.y + 5) || 0}px;

  transition: width .3s ease,
              left .3s ease,
              height .3s ease;

  ${p => !p.bounds && css`
    display: none;
  `}

  filter: drop-shadow(0px -10px 27px rgba(0, 0, 0, 0.09))
          drop-shadow(0px 3.75998px 11.28px rgba(0, 0, 0, 0.0503198))
          drop-shadow(0px 2.01027px 6.0308px rgba(0, 0, 0, 0.0417275))
          drop-shadow(0px 1.12694px 3.38082px rgba(0, 0, 0, 0.035))
          drop-shadow(0px 0.598509px 1.79553px rgba(0, 0, 0, 0.0282725))
          drop-shadow(0px 0.249053px 0.747159px rgba(0, 0, 0, 0.0196802));

  &.fade-enter {
    transform-origin: top;
    transform: scale(0);
  }

  &.fade-enter-active {
    transform: scale(1);
    transition: transform .2s ease;
  }

  &.fade-exit {
    transform-origin: top;
    transform: scale(1);
    transition: transform .2s ease-out;
  }

  &.fade-exit-active {
    transform: scale(0);
  }
`;

const TopArrow = styled.div`
  position: absolute;
  width: 0; 
  height: 0; 
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid var(--lizzy-white);
  display: ${p => !p.bounds ? 'none' : 'block'};
  top: ${p => p.bounds?.y || 0}px;
  left: ${p => p.bounds?.x || 0}px;
  margin-left: -6px;
  z-index: 14;
`;
